import React, { useEffect, useMemo, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import axiosInstance from '../../api/axiosInstance'

import { ComposableMap, Geographies, Geography } from 'react-simple-maps'
import countries from 'i18n-iso-countries'
import enLocale from 'i18n-iso-countries/langs/en'
import geoData from '../../ext_packs/countries-110m.json'
countries.registerLocale(enLocale)

const geoUrl = geoData

const BLOCK_CLICK_WHEN_CREATED = false

const normalize = (s = '') =>
    s
        .toString()
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/’/g, "'")
        .trim()
        .toLowerCase()

const nameAliasToNaturalEarth = (name = '') => {
    const n = normalize(name)
    const map = {
        'united states': 'united states of america',
        usa: 'united states of america',
        us: 'united states of america',
        uk: 'united kingdom',
        russia: 'russian federation',
        'south korea': 'korea, south',
        'north korea': 'korea, north',
        vietnam: 'viet nam',
        'czech republic': 'czechia',
        'ivory coast': "cote d'ivoire",
        'cape verde': 'cabo verde',
        syria: 'syrian arab republic',
        laos: 'laos',
    }
    return map[n] || n
}

const SearchableSelect = ({
    options,
    value,
    onChange,
    placeholder = 'Search…',
    emptyText = 'No results',
    className = '',
}) => {
    const [open, setOpen] = useState(false)
    const [q, setQ] = useState('')
    const wrapRef = useRef(null)
    const inputRef = useRef(null)

    const filtered = useMemo(() => {
        if (!q.trim()) return options
        const s = q.toLowerCase()
        return options.filter(
            (o) =>
                o.label.toLowerCase().includes(s) ||
                String(o.value).toLowerCase().includes(s)
        )
    }, [options, q])

    useEffect(() => {
        const onDocClick = (e) => {
            if (!wrapRef.current?.contains(e.target)) setOpen(false)
        }
        document.addEventListener('mousedown', onDocClick)
        return () => document.removeEventListener('mousedown', onDocClick)
    }, [])

    useEffect(() => {
        if (open) inputRef.current?.focus()
    }, [open])

    const currentLabel = useMemo(() => {
        const found = options.find((o) => o.value === value)
        return found?.label ?? value ?? ''
    }, [options, value])

    return (
        <div className={`relative ${className}`} ref={wrapRef}>
            <button
                type="button"
                onClick={() => setOpen((s) => !s)}
                className="w-full text-left px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white/90 dark:bg-gray-900/60 hover:bg-white dark:hover:bg-gray-900 transition"
                title={currentLabel || placeholder}
            >
                <span
                    className={`text-sm ${
                        currentLabel
                            ? 'text-gray-900 dark:text-gray-100'
                            : 'text-gray-400'
                    }`}
                >
                    {currentLabel || placeholder}
                </span>
            </button>

            {open && (
                <div className="absolute z-20 mt-2 w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl overflow-hidden">
                    <div className="p-2 border-b border-gray-100 dark:border-gray-800">
                        <input
                            ref={inputRef}
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder={placeholder}
                            className="w-full px-3 py-2 text-sm rounded-md bg-gray-50 dark:bg-gray-800 outline-none focus:ring-2 ring-green-500"
                        />
                    </div>
                    <ul className="max-h-64 overflow-y-auto">
                        {filtered.length === 0 && (
                            <li className="px-3 py-3 text-sm text-gray-500">
                                {emptyText}
                            </li>
                        )}
                        {filtered.map((o) => (
                            <li key={o.value}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        onChange(o.value)
                                        setOpen(false)
                                        setQ('')
                                    }}
                                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-800 ${
                                        value === o.value
                                            ? 'bg-gray-50 dark:bg-gray-800'
                                            : ''
                                    }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="truncate">
                                            {o.label}
                                        </span>
                                        <span className="ml-2 text-xs text-gray-500">
                                            {o.value}
                                        </span>
                                    </div>
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    )
}

const RegionCreatePage = () => {
    const navigate = useNavigate()

    // ====== NEW: build full country options & helper maps ======
    const countryOptions = useMemo(() => {
        const names = countries.getNames('en', { select: 'official' }) // {'US':'United States of America',...}
        const arr = Object.entries(names).map(([code, name]) => ({
            label: name,
            value: code, // ISO-2
        }))
        arr.sort((a, b) => a.label.localeCompare(b.label))
        return arr
    }, [])

    const codeToName = useMemo(() => {
        const map = new Map()
        countryOptions.forEach((o) => map.set(o.value, o.label))
        return map
    }, [countryOptions])

    const nameToCode = useMemo(() => {
        const map = new Map()
        countryOptions.forEach((o) => map.set(normalize(o.label), o.value))
        return map
    }, [countryOptions])

    // ====== state ======
    const [form, setForm] = useState({ code: '', name: '' }) // code=ISO2
    const [loading, setLoading] = useState(false)
    const [selectedCountry, setSelectedCountry] = useState(null)

    // Regions đã tạo để đánh dấu trên map
    const [existingRegions, setExistingRegions] = useState([])
    const [loadingExisting, setLoadingExisting] = useState(true)

    // Fetch existing regions một lần khi mount
    useEffect(() => {
        const fetchExisting = async () => {
            setLoadingExisting(true)
            try {
                const res = await axiosInstance.get('/admin/regions/')
                const data = res?.data || []
                setExistingRegions(Array.isArray(data) ? data : [])
            } catch (err) {
                console.error('❌ Fetch existing regions failed:', err)
            } finally {
                setLoadingExisting(false)
            }
        }
        fetchExisting()
    }, [])

    const existingCodeSet = useMemo(
        () =>
            new Set(
                existingRegions
                    .map((r) => (r?.code ?? '').toString().toLowerCase())
                    .filter(Boolean)
            ),
        [existingRegions]
    )

    const existingNameSet = useMemo(
        () =>
            new Set(
                existingRegions
                    .map((r) => nameAliasToNaturalEarth(r?.name))
                    .filter(Boolean)
            ),
        [existingRegions]
    )

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!form.code || !form.name) {
            alert('Please select both Country Name and Country Code (ISO-2).')
            return
        }
        setLoading(true)
        try {
            await axiosInstance.post('/admin/regions/', form)
            // reset
            setForm({ code: '', name: '' })
            setSelectedCountry(null)
        } catch (err) {
            console.error('❌ Create failed:', err)
            alert('🚫 Failed to create region')
        } finally {
            setLoading(false)
        }
    }

    const handleCountryClick = (geo) => {
        const neNameRaw = geo.properties?.name ?? ''
        const neNameNorm = nameAliasToNaturalEarth(neNameRaw)

        const iso2 = nameToCode.get(normalize(neNameNorm)) || ''

        const fallbackIso2 =
            iso2 ||
            countries.getAlpha2Code(neNameRaw, 'en') ||
            countries.getAlpha2Code(neNameNorm, 'en') ||
            ''

        const finalCode = fallbackIso2
        const finalName = finalCode
            ? codeToName.get(finalCode) || neNameRaw
            : neNameRaw

        const alreadyByCode =
            finalCode && existingCodeSet.has(finalCode.toLowerCase())
        const alreadyByName = existingNameSet.has(neNameNorm)
        if ((alreadyByCode || alreadyByName) && BLOCK_CLICK_WHEN_CREATED) return

        setSelectedCountry(geo.id)
        setForm({ code: finalCode, name: finalName })
    }

    const getFill = (geo) => {
        const isSelected = selectedCountry === geo.id
        const neNameNorm = nameAliasToNaturalEarth(geo.properties?.name ?? '')
        const iso2 = nameToCode.get(normalize(neNameNorm)) || ''
        const isCreated =
            (iso2 && existingCodeSet.has(iso2.toLowerCase())) ||
            existingNameSet.has(neNameNorm)

        if (isSelected) return '#22c55e' // green-500
        if (isCreated) return '#f59e0b' // amber-500
        return '#9ca3af' // gray-400
    }

    // ====== NEW: dropdown data ======
    const nameOptions = useMemo(
        () => countryOptions.map((o) => ({ label: o.label, value: o.value })), // value=ISO2
        [countryOptions]
    )
    const codeOptions = useMemo(
        () =>
            countryOptions.map((o) => ({
                label: `${o.label} (${o.value})`,
                value: o.value, // ISO2
            })),
        [countryOptions]
    )

    // ====== NEW: sync 2 chiều ======
    const selectByCode = (iso2) => {
        const name = codeToName.get(iso2) || ''
        setForm({ code: iso2, name })
    }
    const selectByNameIso2 = (iso2) => {
        const name = codeToName.get(iso2) || ''
        setForm({ code: iso2, name })
    }

    return (
        <div className="text-white">
            <main>
                <div className="mx-auto rounded-2xl shadow-xl ">
                    <div className="flex items-center justify-between gap-4 mb-6">
                        <h2 className="text-3xl font-bold text-green-400 drop-shadow-md">
                            Create Region
                        </h2>

                        {/* Legend */}
                        <div className="flex items-center gap-4 text-sm">
                            <span className="inline-flex items-center gap-2">
                                <span
                                    className="inline-block w-3 h-3 rounded-sm"
                                    style={{ background: '#22c55e' }}
                                />
                                Selected
                            </span>
                            <span className="inline-flex items-center gap-2">
                                <span
                                    className="inline-block w-3 h-3 rounded-sm"
                                    style={{ background: '#f59e0b' }}
                                />
                                Already created
                            </span>
                            <span className="inline-flex items-center gap-2">
                                <span
                                    className="inline-block w-3 h-3 rounded-sm"
                                    style={{ background: '#9ca3af' }}
                                />
                                Available
                            </span>
                        </div>
                    </div>

                    {/* Map Picker */}
                    <div className="mb-8">
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-gray-300">
                                🌍 Click a country to auto-fill.{' '}
                                {loadingExisting ? (
                                    <span className="text-gray-400">
                                        Loading existing…
                                    </span>
                                ) : (
                                    <span className="text-gray-400">
                                        Marked <b>{existingRegions.length}</b>{' '}
                                        created region(s).
                                    </span>
                                )}
                            </p>
                        </div>

                        <div className="w-full border border-gray-700 rounded-xl overflow-hidden bg-gray-900">
                            <ComposableMap projectionConfig={{ scale: 140 }}>
                                <Geographies geography={geoUrl}>
                                    {({ geographies }) =>
                                        geographies.map((geo) => {
                                            const fill = getFill(geo)
                                            const neNameNorm =
                                                nameAliasToNaturalEarth(
                                                    geo.properties?.name ?? ''
                                                )
                                            const iso2 =
                                                nameToCode.get(
                                                    normalize(neNameNorm)
                                                ) || ''
                                            const isCreated =
                                                (iso2 &&
                                                    existingCodeSet.has(
                                                        iso2.toLowerCase()
                                                    )) ||
                                                existingNameSet.has(neNameNorm)

                                            return (
                                                <Geography
                                                    key={geo.rsmKey}
                                                    geography={geo}
                                                    onClick={() =>
                                                        handleCountryClick(geo)
                                                    }
                                                    style={{
                                                        default: {
                                                            fill,
                                                            cursor:
                                                                isCreated &&
                                                                BLOCK_CLICK_WHEN_CREATED
                                                                    ? 'not-allowed'
                                                                    : 'pointer',
                                                            stroke: '#111827',
                                                            strokeWidth: 0.5,
                                                            outline: 'none',
                                                            transition:
                                                                'fill 150ms ease',
                                                        },
                                                        hover: {
                                                            fill:
                                                                fill ===
                                                                '#22c55e'
                                                                    ? '#22c55e'
                                                                    : fill ===
                                                                      '#f59e0b'
                                                                    ? '#d97706'
                                                                    : '#16a34a',
                                                            outline: 'none',
                                                        },
                                                        pressed: {
                                                            fill: '#22c55e',
                                                            outline: 'none',
                                                        },
                                                    }}
                                                />
                                            )
                                        })
                                    }
                                </Geographies>
                            </ComposableMap>
                        </div>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* NEW: Country Name (searchable) */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                Country Name
                            </label>
                            <SearchableSelect
                                options={nameOptions}
                                value={form.code /* value là ISO2 */}
                                onChange={selectByNameIso2}
                                placeholder="Search & select country name…"
                            />
                        </div>

                        {/* NEW: Country Code ISO-2 (searchable) */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                Country Code (ISO-2)
                            </label>
                            <SearchableSelect
                                options={codeOptions}
                                value={form.code}
                                onChange={selectByCode}
                                placeholder="Search & select country code…"
                            />
                        </div>

                        {/* Readonly preview of chosen name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                Selected Name
                            </label>
                            <input
                                type="text"
                                readOnly
                                value={form.name}
                                className="w-full rounded-lg bg-gray-900 border border-gray-700 px-4 py-2 text-gray-200"
                                placeholder="—"
                            />
                        </div>

                        {/* Actions */}
                        <div className="flex justify-end space-x-4">
                            <button
                                type="button"
                                onClick={() => navigate(-1)}
                                className="px-5 py-2 rounded-lg bg-gray-600 hover:bg-gray-500 text-white font-semibold transition"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-5 py-2 rounded-lg bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold shadow-lg transition disabled:opacity-50"
                            >
                                {loading ? '⏳ Creating...' : 'Create'}
                            </button>
                        </div>
                    </form>
                </div>
            </main>
        </div>
    )
}

export default RegionCreatePage
